/**
 * CategoriesTableV2 — Categories list, ported from the prototype's
 * `.screen-categories` (cms-redesign-mockup.html ~line 6756+, styling
 * ~1724–2009).
 *
 * Layout: two sections rendered top-to-bottom:
 *   1. Audio Categories      — !is_quick_tap && !is_audiobook
 *   2. Quick Tap Categories  —  is_quick_tap
 *
 * Audiobook categories are intentionally excluded from this list —
 * audiobooks live under the Audiobooks page and the v1 CMS hides them
 * here too (CategoriesTable.js line 69: `.filter(el => !el.category_is_audiobook)`).
 *
 * Drag-to-reorder uses @dnd-kit (already in the bundle). Each section has
 * its own SortableContext so a drag is scoped within a type — Audio rows
 * can only reorder amongst Audio, Quick Tap amongst Quick Tap. The full
 * non-audiobook list (in its new order) is sent to PATCH /categories/sort
 * via the existing `sortCategories` action — same payload shape v1 uses.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  fetchAllCategories,
  clearCategories,
  sortCategories,
} from '../../../actions/categoryActions';
import {
  LibTable,
  LibSection,
  StatusPill,
  IconButton,
  cn,
} from '../../redesign';

/* ─── Icons (kept inline; brand pack doesn't ship a drag-handle glyph) ── */

const DragHandleIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="9"  cy="6"  r="0.5" />
    <circle cx="9"  cy="12" r="0.5" />
    <circle cx="9"  cy="18" r="0.5" />
    <circle cx="15" cy="6"  r="0.5" />
    <circle cx="15" cy="12" r="0.5" />
    <circle cx="15" cy="18" r="0.5" />
  </svg>
);

const EditIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);

/* ─── Field adapters ───────────────────────────────────────────────────── */

function isQuickTap(c) { return !!c.category_is_quick_tap; }
function sortBySortOrder(a, b) {
  const av = Number(a.category_sort_order ?? a.category_sort_number ?? 0);
  const bv = Number(b.category_sort_order ?? b.category_sort_number ?? 0);
  return av - bv;
}

/* ─── Inline flags (Asap Condensed pills next to the title) ────────────── */
function InlineFlag({ tone, children }) {
  const BG = {
    interest: '#E58A6A',
    dark:     '#2C3E50',
    qtap:     '#75A293',
    audiobook:'#C4715B',
  };
  return (
    <span
      className="inline-flex items-center font-condensed font-bold uppercase text-white rounded-pill shrink-0"
      style={{
        fontSize: 9.5,
        letterSpacing: '0.08em',
        padding: '1px 6px',
        marginLeft: 4,
        background: BG[tone] || BG.dark,
      }}
    >
      {children}
    </span>
  );
}

/* ─── Sortable row ─────────────────────────────────────────────────────
 *  Each <tr> registers with dnd-kit via useSortable. The drag handle is
 *  the only spot that captures pointer events for dragging (we pass
 *  `listeners` down to the handle), so the rest of the row stays
 *  clickable / hoverable. */
function SortableCategoryRow({ category }) {
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.category_id });

  const inactive = !category.category_is_active;

  // dnd-kit's transform helper resolves to a CSS string. We also apply a
  // mild visual lift while dragging so the user sees the grabbed row.
  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    zIndex: isDragging ? 5 : undefined,
  };

  return (
    <LibTable.Row inactive={inactive} ref={setNodeRef} style={dragStyle} {...attributes}>
      {/* Drag handle cell — `listeners` attached here so dragging is
       *  initiated from the handle only, not the whole row. */}
      <td
        className="border-b border-line text-center group-data-[inactive=true]:bg-[var(--color-row-inactive)] group-data-[inactive=true]:group-hover:bg-[var(--color-row-inactive-hover)]"
        style={{ width: 36, padding: '10px 0 10px 10px' }}
      >
        <span
          {...listeners}
          aria-label="Drag to reorder"
          tabIndex={0}
          className={cn(
            'inline-grid place-items-center text-text-muted cursor-grab',
            'opacity-[0.35] group-hover:opacity-100',
            'hover:bg-cream hover:text-navy group-data-[inactive=true]:hover:bg-[var(--color-row-inactive-hover)] active:cursor-grabbing',
            'focus:outline-none focus-visible:bg-cream group-data-[inactive=true]:focus-visible:bg-[var(--color-row-inactive-hover)] focus-visible:text-navy',
          )}
          style={{
            width: 26,
            height: 26,
            borderRadius: 4,
            transition: 'all 140ms ease',
            touchAction: 'none',
          }}
        >
          <span style={{ width: 13, height: 13, display: 'inline-flex' }}>
            {DragHandleIcon}
          </span>
        </span>
      </td>

      {/* Category — cover + title + inline flags. */}
      <LibTable.Td>
        <div className="flex items-center min-w-0" style={{ gap: 8 }}>
          <div
            className="bg-cream border border-sand shrink-0"
            style={{
              width: 28,
              height: 28,
              borderRadius: 4,
              backgroundImage: category.category_image
                ? `url('${category.category_image}')`
                : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          <span className="min-w-0 flex items-center" style={{ gap: 4 }}>
            <span className="font-semibold text-navy truncate" style={{ fontSize: 13.5 }}>
              {category.category_title || 'Untitled'}
            </span>
            {category.category_is_select_interest && (
              <InlineFlag tone="interest">Interest</InlineFlag>
            )}
            {category.category_is_dark_mode && (
              <InlineFlag tone="dark">Dark</InlineFlag>
            )}
          </span>
        </div>
      </LibTable.Td>

      <LibTable.CenterTd>
        <StatusPill active={!inactive} />
      </LibTable.CenterTd>

      <LibTable.NumTd muted={!Number(category.category_num_subs)}>
        {Number(category.category_num_subs) || 0}
      </LibTable.NumTd>

      <LibTable.NumTd muted={!Number(category.category_num_sessions)}>
        {Number(category.category_num_sessions) || 0}
      </LibTable.NumTd>

      <LibTable.Td style={{ textAlign: 'right' }}>
        <div className="inline-flex opacity-0 transition-opacity duration-150 group-hover:opacity-100">
          <Link
            to={`/category/edit/${category.category_id}`}
            aria-label={`Edit ${category.category_title}`}
          >
            <IconButton size="sm" aria-label="Edit">{EditIconSvg}</IconButton>
          </Link>
        </div>
      </LibTable.Td>
    </LibTable.Row>
  );
}

/* ─── Section (table with its own SortableContext) ───────────────────── */

const HINT = 'Drag any row to reorder · category_sort_order';

function CategorySection({ title, categories, first }) {
  const ids = useMemo(() => categories.map((c) => c.category_id), [categories]);
  return (
    <LibSection title={title} count={categories.length} hint={HINT} first={first}>
      {categories.length === 0 ? (
        <div
          className="bg-surface border border-sand text-text-muted italic text-center"
          style={{ borderRadius: 12, padding: '20px 16px', fontSize: 13 }}
        >
          No categories in this section yet.
        </div>
      ) : (
        <LibTable
          fixed
          className="border border-sand"
          style={{ borderRadius: 12, overflow: 'hidden' }}
        >
          <colgroup>
            <col style={{ width: 36 }} />
            <col />
            <col style={{ width: '13%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '12%' }} />
          </colgroup>
          <LibTable.Head>
            <tr>
              <LibTable.Th aria-label="Drag to reorder"> </LibTable.Th>
              <LibTable.Th>Category</LibTable.Th>
              <LibTable.CenterTh>Status</LibTable.CenterTh>
              <LibTable.NumTh>Subcategories</LibTable.NumTh>
              <LibTable.NumTh>Sessions</LibTable.NumTh>
              <LibTable.Th style={{ textAlign: 'right' }}>Actions</LibTable.Th>
            </tr>
          </LibTable.Head>
          {/* Each section gets its own SortableContext, scoped to the IDs
           *  visible in that section. Combined with the cross-section
           *  guard in handleDragEnd below, this enforces "drag stays
           *  within a type" (v1 parity). */}
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <tbody>
              {categories.map((c) => (
                <SortableCategoryRow key={c.category_id} category={c} />
              ))}
            </tbody>
          </SortableContext>
        </LibTable>
      )}
    </LibSection>
  );
}

/* ─── Main component ──────────────────────────────────────────────────── */

export default function CategoriesTableV2() {
  const dispatch = useDispatch();
  const allCategories = useSelector((s) => s.categories.all_categories);

  // Local ordered list — source of truth for the displayed (and draggable)
  // order. Initialized from Redux on every `allCategories` change, so any
  // server-side refetch (e.g. after a sortCategories failure) re-syncs us.
  // Drag-end mutates this directly; we don't write back to Redux because
  // sortCategories doesn't dispatch a SUCCESS reducer case.
  const [localList, setLocalList] = useState([]);

  useEffect(() => {
    dispatch(clearCategories());
    dispatch(fetchAllCategories());
  }, [dispatch]);

  useEffect(() => {
    if (!allCategories) return;
    const visible = allCategories
      .filter((c) => !c.category_is_audiobook)
      .sort(sortBySortOrder);
    setLocalList(visible);
  }, [allCategories]);

  // dnd-kit sensors — same setup v1 uses (pointer + keyboard accessibility).
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const activeCat = localList.find((c) => c.category_id === active.id);
      const overCat   = localList.find((c) => c.category_id === over.id);
      if (!activeCat || !overCat) return;

      // Same-section guard — v1 parity. Cross-type drops are no-ops because
      // the category type (quick_tap vs audio) maps to a different FK
      // namespace on subcategory/session children.
      if (isQuickTap(activeCat) !== isQuickTap(overCat)) return;

      const oldIndex = localList.findIndex((c) => c.category_id === active.id);
      const newIndex = localList.findIndex((c) => c.category_id === over.id);
      const next = arrayMove(localList, oldIndex, newIndex);
      setLocalList(next);

      // Same payload shape as v1: array of category_ids in the new order.
      // `sortCategories` POSTs PATCH /categories/sort and re-fetches on
      // failure, which our `allCategories` useEffect picks up and uses
      // to re-sync localList to the server's authoritative order.
      dispatch(sortCategories(next.map((c) => c.category_id)));
    },
    [localList, dispatch],
  );

  // Partition the local list for rendering. Audio first, then Quick Tap.
  const { audio, quickTap } = useMemo(() => {
    const buckets = { audio: [], quickTap: [] };
    for (const c of localList) {
      if (isQuickTap(c)) buckets.quickTap.push(c);
      else                buckets.audio.push(c);
    }
    return buckets;
  }, [localList]);

  if (!allCategories) {
    return (
      <div className="flex items-center justify-center text-text-muted" style={{ height: 256 }}>
        Loading categories…
      </div>
    );
  }

  return (
    <div style={{ padding: '0 28px 32px' }}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <CategorySection
          first
          title="Audio Categories"
          categories={audio}
        />
        <CategorySection
          title="Quick Tap Categories"
          categories={quickTap}
        />
      </DndContext>
    </div>
  );
}
